'use client';

import * as React from 'react';

import { CloseButton } from '@/components/ui/close-button';
import { MODAL_ANIMATION_DURATION, ModalShell } from '@/components/ui/modal-shell';
import { cn } from '@/lib/utils';

// Re-export for backward compatibility — consumers historically imported this
// constant from base-modal. The single source of truth now lives in modal-shell.
export { MODAL_ANIMATION_DURATION };

interface BaseModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /**
   * Callback when modal should close.
   * The parent should update isOpen to false in this callback.
   * If you need to veto the close (e.g., unsaved changes warning),
   * check in this callback and keep isOpen=true if needed.
   */
  onClose: () => void;
  /**
   * Modal title (can be string or custom React element)
   * If passing a complex React element, provide ariaLabel for accessibility
   */
  title: React.ReactNode;
  /** Optional subtitle/description (can be string or custom React element) */
  subtitle?: React.ReactNode;
  /** Modal size (max-width at md+; mobile is full-width within the gutter). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * When true, the modal fills the viewport on mobile (edge-to-edge, full height,
   * square corners, respecting safe-area insets) and restores the centered `size`
   * card at `md+`. Default `false` keeps the centered card on every viewport.
   */
  mobileFullScreen?: boolean;
  /**
   * Modal variant defining typography and spacing
   * @default 'default'
   */
  variant?: ModalVariant;
  /**
   * Modal body content. Optional so header-only modals (e.g., ConfirmDialog,
   * which keeps title + subtitle in the header and actions in the footer)
   * can omit the body region entirely rather than render an empty padded div.
   */
  children?: React.ReactNode;
  /**
   * Optional footer content (typically buttons).
   * BaseModal applies responsive flex layout.
   */
  footer?: React.ReactNode;
  /** Show close button (X) in header */
  showCloseButton?: boolean;
  /** Allow closing with Escape key */
  closeOnEscape?: boolean;
  /** Allow closing by clicking backdrop */
  closeOnBackdrop?: boolean;
  /** Prevent closing (e.g., during form submission) */
  preventCloseWhile?: boolean;
  /** Optional icon in header */
  headerIcon?: React.ReactNode;
  /** Callback after close animation completes */
  onAfterClose?: () => void;
  /** Custom className for modal container */
  className?: string;
  /**
   * ARIA label for the modal.
   * When provided, sets aria-label on the dialog element directly.
   * When omitted, aria-labelledby points to the rendered heading element.
   */
  ariaLabel?: string;
  /**
   * ARIA description for the modal (aria-describedby)
   * Automatically extracted from subtitle if it's a string.
   */
  ariaDescription?: string;
  /** Ref to the modal dialog element (for focus trap, etc.) */
  modalRef?: React.RefObject<HTMLDivElement | null>;
  /** Custom z-index for the modal wrapper (default: 90) */
  zIndex?: number;
  /**
   * ARIA role for the dialog element.
   * - 'dialog' (default): standard modal
   * - 'alertdialog': interrupting modal requiring a decision
   */
  role?: 'dialog' | 'alertdialog';
  /**
   * Custom className for the title element (escape hatch)
   * @deprecated Prefer using variant prop for consistent styling
   */
  titleClassName?: string;
  /**
   * Custom className for the subtitle element (escape hatch)
   * @deprecated Prefer using variant prop for consistent styling
   */
  subtitleClassName?: string;
}

export type { BaseModalProps };

const variantStyles = {
  default: {
    title: 'text-xl font-medium text-balance',
    subtitle: 'text-sm mt-1 text-pretty whitespace-pre-line',
  },
  tour: {
    title: 'text-xl sm:text-2xl font-medium text-balance',
    subtitle: 'text-sm sm:text-base mt-1 text-pretty whitespace-pre-line',
  },
  compact: {
    title: 'text-lg font-medium text-balance',
    subtitle: 'text-xs mt-1 text-pretty whitespace-pre-line',
  },
  prominent: {
    title: 'text-2xl sm:text-3xl font-medium text-balance',
    subtitle: 'text-base sm:text-lg mt-1 text-pretty whitespace-pre-line',
  },
} as const;

type ModalVariant = keyof typeof variantStyles;

/**
 * Helper to extract string from React.ReactNode for ARIA labels
 * Returns the node if it's a string/number, otherwise returns undefined
 */
function getTextFromNode(node: React.ReactNode): string | undefined {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  return undefined;
}

/**
 * Base modal component with consistent animation, backdrop, and close behavior.
 * Thin composer over {@link ModalShell} that adds the standard header + body + footer.
 */
export function BaseModal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  mobileFullScreen = false,
  variant = 'default',
  children,
  footer,
  showCloseButton = true,
  closeOnEscape = true,
  closeOnBackdrop = true,
  preventCloseWhile = false,
  headerIcon,
  onAfterClose,
  className,
  ariaLabel,
  ariaDescription,
  modalRef,
  zIndex = 90,
  role = 'dialog',
  titleClassName,
  subtitleClassName,
}: BaseModalProps) {
  const styles = variantStyles[variant];
  const titleClasses = cn(styles.title, titleClassName);
  const subtitleClasses = cn(styles.subtitle, subtitleClassName);

  const generatedId = React.useId();
  const headingId = `modal-${generatedId}`;

  const subtitleText = getTextFromNode(subtitle);
  const descriptionId =
    ariaDescription ?? (subtitleText ? `modal-description-${generatedId}` : undefined);

  const hasBody = React.Children.toArray(children).length > 0;

  const handleClose = React.useCallback(() => {
    if (preventCloseWhile) return;
    onClose();
  }, [preventCloseWhile, onClose]);

  // Conditional spreads are required under exactOptionalPropertyTypes: an
  // explicit `key: undefined` doesn't satisfy an `optional key without
  // undefined in the value type` (see billing-gate.tsx:74-79 for the reference
  // pattern).
  const shellProps = {
    isOpen,
    onClose,
    size,
    mobileFullScreen,
    closeOnEscape,
    closeOnBackdrop,
    preventCloseWhile,
    zIndex,
    role,
    ...(onAfterClose ? { onAfterClose } : {}),
    ...(className ? { className } : {}),
    ...(ariaLabel ? { ariaLabel } : { ariaLabelledBy: headingId }),
    ...(descriptionId ? { ariaDescribedBy: descriptionId } : {}),
    ...(modalRef ? { modalRef } : {}),
  };

  return (
    <ModalShell {...shellProps}>
      {/* Header */}
      <div className="p-6" style={{ borderBottom: '1px solid var(--border-default)' }}>
        {/* With a subtitle the header is several lines tall: keep the X level
            with the title rather than at the header's vertical middle. A
            title-only header keeps its centred X. */}
        <div className={cn('flex justify-between', subtitle ? 'items-start' : 'items-center')}>
          <div className="flex-1 min-w-0">
            {headerIcon && (
              <div className="flex items-center gap-3 mb-2">
                {headerIcon}
                <h2
                  id={headingId}
                  className={titleClasses}
                  style={{ color: 'var(--text-primary)' }}
                >
                  {title}
                </h2>
              </div>
            )}
            {!headerIcon && (
              <h2 id={headingId} className={titleClasses} style={{ color: 'var(--text-primary)' }}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                id={descriptionId}
                className={subtitleClasses}
                style={{ color: 'var(--text-secondary)' }}
              >
                {subtitle}
              </p>
            )}
          </div>
          {showCloseButton && (
            <CloseButton
              aria-label="Close modal"
              onClick={handleClose}
              disabled={preventCloseWhile}
              className="ml-4"
            />
          )}
        </div>
      </div>

      {/*
        Body — omitted entirely for header-only modals like ConfirmDialog.
        `React.Children.toArray` filters `null`, `undefined`, and booleans,
        so the body is correctly omitted for conditional children patterns
        like `{condition && <X/>}` when condition is false.
      */}
      {hasBody && (
        <div className="flex-1 overflow-y-auto p-6" style={{ color: 'var(--text-primary)' }}>
          {children}
        </div>
      )}

      {/* Footer — border-top only when a body separates it from the header */}
      {footer && (
        <div
          className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-between gap-3 p-6"
          style={hasBody ? { borderTop: '1px solid var(--border-default)' } : undefined}
        >
          {footer}
        </div>
      )}
    </ModalShell>
  );
}
